import { ArrowLeftIcon, SparklesIcon } from '@heroicons/react/24/outline';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { ActionsTab } from '../components/patient-files/actions/ActionsTab';
import { DmControlRoute } from '../components/patient-files/actions/dm/control/DmControlRoute';
import { AnalysisTab } from '../components/patient-files/analysis/AnalysisTab';
import { ChatTab } from '../components/patient-files/ChatTab';
import ConversationActionsBar from '../components/conversations/ConversationActionsBar';
import { ChatModeToggle } from '../components/conversations/ChatModeToggle';
import { useChatStatus } from '../hooks/useChatStatus';
import { FilesTabContent } from '../components/patient-files/FilesTabContent';
import { InfoTab } from '../components/patient-files/InfoTab';
import { NotesTab } from '../components/patient-files/notes/NotesTab';
import { MonitoringTabBadge, useChatApiVersion } from '@odo/shared-ui';
import { dmScansQueryConfig } from '../services/dmScans';
import { MonitoringTabPanel } from '../components/patient-files/monitoring/MonitoringTabPanel';
import { OverviewTab } from '../components/patient-files/overview/OverviewTab';
import { CollapsiblePatientCard } from '../components/patient-files/CollapsiblePatientCard';
import { PatientInfoPanel } from '../components/patient-files/PatientInfoPanel';
import { TimelineRail } from '../components/patient-files/timeline-rail/TimelineRail';
import { usePatientCard } from '../hooks/usePatientCard';
import { usePatientAssetsCount, usePatientAssets } from '../hooks/usePatientAssets';
import { usePatientNotes } from '../hooks/usePatientNotes';
import { usePageTitle } from '../hooks/usePageTitle';
import { formatDoctorLine } from '../components/accounts/practices/practiceFormatting';
import { PatientChatProvider } from '@odo/shared-ui';
import { getChatService } from '../services/chatService';
import { usePageHeader } from '../providers/PageHeaderProvider';
import { buildWorkQueueCaseUrl } from '../utils/workQueueLink';
import { type PatientDetail } from '../services/patients';
import type { ChatType } from '../services/chat';
import type { GetPatientResponse } from '../services/patientCard';

// Adapts the v2 card payload into the PatientDetail shape PatientInfoPanel
// consumes, replacing the Monday-sourced `fetchPatient` call.
function cardToPatientDetail(card: GetPatientResponse): PatientDetail {
    return {
        id: card.id,
        name: card.name,
        patient_email: card.email ?? undefined,
        patient_phone: card.phone ?? undefined,
        patient_date_of_birth: card.dateOfBirth ?? undefined,
        patient_gender: card.gender ?? undefined,
        patient_case_status: card.status?.caseStatus ?? undefined,
        patient_case_type: card.treatment?.caseType ?? undefined,
        patient_sub_case_type: card.treatment?.subCaseType ?? undefined,
        patient_orthopulse_status: card.orthopulse?.status ?? undefined,
        patient_manufacturer_link: card.treatment?.manufacturerLink ?? undefined,
        patient_dm_link: card.monitoring?.dmLink ?? undefined,
        odo_internal_case_status: card.status?.internalCaseStatus ?? undefined,
        user_related_practice_reference: card.practice ? [card.practice.id] : null,
        orthoItemId: card.boards?.orthoItemId ?? undefined
    };
}

const VALID_TABS = [
    'overview',
    'monitoring',
    'files',
    'conversations',
    'actions',
    'notes',
    'analysis',
    'info'
] as const;
type Tab = (typeof VALID_TABS)[number];

function readTabFromSearch(search: string): Tab {
    const raw = new URLSearchParams(search).get('tab');
    return (VALID_TABS as readonly string[]).includes(raw ?? '') ? (raw as Tab) : 'overview';
}

function readActionFromSearch(search: string): string | null {
    return new URLSearchParams(search).get('action');
}

function readPreviewFromSearch(search: string): string[] | null {
    const raw = new URLSearchParams(search).get('preview');
    if (!raw) return null;
    const ids = raw
        .split(',')
        .map((s) => s.trim())
        .filter(Boolean);
    return ids.length > 0 ? ids : null;
}

const PatientFiles: React.FC = () => {
    // Part of the provider key, alongside chatMode: v1 ids are Monday update
    // ids and v2 ids are UUIDs, so the provider must rebuild rather than
    // reconcile its cached messages onto ids from the other world.
    const { version: chatApiVersion } = useChatApiVersion();
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const location = useLocation();
    const { setTitle: setPageHeaderTitle } = usePageHeader();

    const navigationState = location.state as {
        patientName?: string;
        workQueueSearch?: string;
    } | null;
    const [activeTab, setActiveTab] = useState<Tab>(() => readTabFromSearch(location.search));
    const [chatMode, setChatMode] = useState<ChatType>('external');
    // Track which tabs have been visited so we mount them lazily but keep them alive
    const [mountedTabs, setMountedTabs] = useState<Set<string>>(
        () => new Set([readTabFromSearch(location.search)])
    );

    const { isUnread: hasUnreadExternal } = useChatStatus(id ?? '', 'external');
    const { isUnread: hasUnreadInternal } = useChatStatus(id ?? '', 'internal');

    // Sync activeTab with URL (handles browser back/forward navigation)
    useEffect(() => {
        const next = readTabFromSearch(location.search);
        if (next !== activeTab) {
            setActiveTab(next);
            setMountedTabs((s) => new Set(s).add(next));
        }
    }, [location.search, activeTab]);

    const {
        data: cardData,
        isLoading: isCardLoading,
        error: cardError,
        refetch: refetchCard
    } = usePatientCard(id);

    const filesTabMounted = mountedTabs.has('files');
    const totalFiles = usePatientAssetsCount(id, { enabled: filesTabMounted });
    const { refresh: refreshAssets } = usePatientAssets(id, {
        enabled: false
    });

    const { notes } = usePatientNotes(id);
    const notesCount = notes.length;
    const notesCritical = notes.some((n) => n.severity === 'critical');

    const patientDetail = useMemo<PatientDetail | null>(() => {
        if (!cardData) return null;
        return cardToPatientDetail(cardData);
    }, [cardData]);

    const practice = cardData?.practice;
    const practiceName = practice?.name ?? null;

    const doctorName = useMemo(() => {
        if (!practice) return null;
        return formatDoctorLine(`${practice.drFirstName ?? ''} ${practice.drLastName ?? ''}`);
    }, [practice]);

    const mondayLinks = useMemo(() => {
        if (!cardData?.boards) return undefined;
        const base = 'https://ondemandorthodontist.monday.com/boards';
        const orthoItemId = cardData.boards?.orthoItemId;
        return {
            patientBoardUrl: `${base}/${cardData.boards.patientBoardId}/pulses/${cardData.id}`,
            orthoBoardUrl: orthoItemId
                ? `${base}/${cardData.boards.orthoBoardId}/pulses/${orthoItemId}`
                : null
        };
    }, [cardData]);

    // Absolute so the Links section can open it in a new tab without disturbing
    // this tab's navigation history.
    const workQueueUrl = useMemo(
        () => (id ? buildWorkQueueCaseUrl(id, { absolute: true }) : null),
        [id]
    );

    const previewFileIds = useMemo(() => readPreviewFromSearch(location.search), [location.search]);

    // Reflect the file(s) currently open in the Files preview into the `preview`
    // URL param (comma-separated for multi-STL), or drop it when back on the list.
    const handlePreviewChange = useCallback(
        (fileIds: string[] | null) => {
            const params = new URLSearchParams(location.search);
            const current = params.get('preview');
            const next = fileIds && fileIds.length > 0 ? fileIds.join(',') : null;

            if (current === next) return;
            if (next) {
                params.set('preview', next);
            } else {
                params.delete('preview');
            }
            const search = params.toString();
            const url = `${location.pathname}${search ? `?${search}` : ''}`;

            navigate(url, { replace: next === null });
        },
        [navigate, location.pathname, location.search]
    );

    const patientName = cardData?.name ?? navigationState?.patientName ?? '';

    usePageTitle(patientName || null);

    useEffect(() => {
        if (patientName) setPageHeaderTitle(patientName);
        return () => setPageHeaderTitle(null);
    }, [patientName, setPageHeaderTitle]);

    const isLoading = isCardLoading;
    const hasError = !!cardError || (!isCardLoading && !cardData);

    if (isLoading) {
        return (
            <div className="flex-1 flex items-center justify-center">
                <div className="w-8 h-8 rounded-full border-2 border-gray-300 border-t-gray-800 animate-spin" />
            </div>
        );
    }

    if (hasError || !patientDetail) {
        return (
            <div className="flex-1 flex flex-col items-center justify-center gap-3">
                <div className="text-sm text-red-500">Failed to load patient</div>
                <button
                    onClick={() => refetchCard()}
                    className="px-3 py-1.5 text-xs font-medium text-gray-700 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
                >
                    Retry
                </button>
            </div>
        );
    }

    return (
        <div className="flex flex-col h-full w-full px-8 py-4 gap-3">
            {/* Breadcrumb pushes the work queue with this case selected so Back returns here;
                the saved queue search restores its filters, and ?case= pins the case regardless. */}
            <button
                onClick={() => {
                    const search = new URLSearchParams(navigationState?.workQueueSearch ?? '');
                    if (id) search.set('case', id);
                    navigate(`/work-queue?${search.toString()}`);
                }}
                className="inline-flex items-center gap-1.5 text-gray-500 hover:text-gray-800 transition-colors text-xs font-medium self-start"
            >
                <ArrowLeftIcon className="w-3 h-3" />
                Patients
                <span className="text-gray-300 mx-0.5">/</span>
                <span className="text-gray-700">{patientName}</span>
            </button>

            {/* Two-card layout */}
            <div className="flex-1 min-h-0 flex gap-4 overflow-hidden relative">
                {/* Left: patient card (collapsible, persists state in localStorage) */}
                {patientDetail && (
                    <CollapsiblePatientCard
                        patientName={patientDetail.name}
                        patientEmail={patientDetail.patient_email}
                        doctorName={doctorName}
                        practiceName={practiceName}
                        profilePictureUrl={cardData?.profilePictureUrl ?? undefined}
                        profileThumbnailUrl={cardData?.profileThumbnailUrl ?? undefined}
                    >
                        {(collapseButton) => (
                            <PatientInfoPanel
                                patient={patientDetail}
                                practiceName={practiceName}
                                practiceId={cardData?.practice?.id ?? null}
                                doctorName={doctorName}
                                mondayLinks={mondayLinks}
                                workQueueUrl={workQueueUrl}
                                manufacturer={cardData?.treatment?.manufacturer ?? null}
                                profilePictureUrl={cardData?.profilePictureUrl ?? undefined}
                                profileThumbnailUrl={cardData?.profileThumbnailUrl ?? undefined}
                                version={cardData?.status?.version ?? null}
                                headerAction={collapseButton}
                            />
                        )}
                    </CollapsiblePatientCard>
                )}

                {/* Right: Tabbed content (75%) */}
                <div className="flex-1 min-w-0 bg-white border border-gray-200 rounded-xl shadow-[0px_1px_2px_rgba(0,0,0,0.08)] flex flex-col overflow-hidden">
                    {/* Tab bar */}
                    <div className="flex items-center gap-5 px-5 border-b border-gray-200 flex-shrink-0">
                        {VALID_TABS.map((tab) => (
                            <button
                                key={tab}
                                onClick={() => {
                                    setActiveTab(tab);
                                    setMountedTabs((s) => new Set(s).add(tab));
                                    const params = new URLSearchParams(location.search);
                                    if (tab === 'overview') {
                                        params.delete('tab');
                                    } else {
                                        params.set('tab', tab);
                                    }
                                    const search = params.toString();
                                    navigate(`${location.pathname}${search ? `?${search}` : ''}`, {
                                        replace: true
                                    });
                                }}
                                className={`py-3 text-sm font-medium transition-colors border-b-2 -mb-px ${
                                    activeTab === tab
                                        ? 'text-gray-900 border-gray-900'
                                        : 'text-gray-400 border-transparent hover:text-gray-600'
                                }`}
                            >
                                {tab === 'overview' ? (
                                    'Overview'
                                ) : tab === 'monitoring' ? (
                                    <span className="inline-flex items-center gap-1.5">
                                        Monitoring
                                        {id && (
                                            <MonitoringTabBadge
                                                queries={dmScansQueryConfig}
                                                patientId={id}
                                                dmId={cardData?.monitoring?.dmId ?? null}
                                            />
                                        )}
                                    </span>
                                ) : tab === 'files' ? (
                                    <>
                                        Files
                                        {totalFiles > 0 && (
                                            <span
                                                className={`ml-1.5 text-[11px] font-medium px-1.5 py-0.5 rounded-full ${
                                                    activeTab === 'files'
                                                        ? 'bg-gray-900 text-white'
                                                        : 'bg-gray-100 text-gray-500'
                                                }`}
                                            >
                                                {totalFiles}
                                            </span>
                                        )}
                                    </>
                                ) : tab === 'conversations' ? (
                                    <span className="inline-flex items-center gap-1.5">
                                        Conversations
                                        {(hasUnreadExternal || hasUnreadInternal) && (
                                            <span
                                                role="status"
                                                aria-label="Unread message"
                                                className="w-1.5 h-1.5 rounded-full bg-amber-500 flex-shrink-0"
                                            />
                                        )}
                                    </span>
                                ) : tab === 'notes' ? (
                                    <span className="inline-flex items-center gap-1.5">
                                        Notes
                                        {notesCritical && (
                                            <span className="w-1.5 h-1.5 rounded-full bg-red-500 flex-shrink-0" />
                                        )}
                                        {notesCount > 0 && (
                                            <span
                                                className={`text-[11px] font-medium px-1.5 py-0.5 rounded-full ${
                                                    notesCritical
                                                        ? 'bg-red-100 text-red-600'
                                                        : activeTab === 'notes'
                                                          ? 'bg-gray-900 text-white'
                                                          : 'bg-gray-100 text-gray-500'
                                                }`}
                                            >
                                                {notesCount}
                                            </span>
                                        )}
                                    </span>
                                ) : tab === 'actions' ? (
                                    'Actions'
                                ) : tab === 'analysis' ? (
                                    <span className="inline-flex items-center gap-1.5">
                                        <SparklesIcon
                                            className={`w-3.5 h-3.5 ${activeTab === 'analysis' ? 'text-gray-700' : 'text-gray-300'}`}
                                        />
                                        Analysis
                                    </span>
                                ) : (
                                    'Info'
                                )}
                            </button>
                        ))}
                    </div>

                    {/* Tab content, lazy-mounted: first visit mounts, then stays alive via hidden */}
                    {id && mountedTabs.has('overview') && (
                        <div
                            className={`flex flex-col flex-1 min-h-0 ${activeTab === 'overview' ? '' : 'hidden'}`}
                        >
                            <OverviewTab
                                patientId={id}
                                onViewNotes={() => {
                                    setActiveTab('notes');
                                    setMountedTabs((s) => new Set(s).add('notes'));
                                    const params = new URLSearchParams(location.search);
                                    params.set('tab', 'notes');
                                    navigate(`${location.pathname}?${params.toString()}`, {
                                        replace: true
                                    });
                                }}
                            />
                        </div>
                    )}

                    {id && mountedTabs.has('monitoring') && (
                        <div
                            className={`flex flex-col flex-1 min-h-0 ${activeTab === 'monitoring' ? '' : 'hidden'}`}
                        >
                            <MonitoringTabPanel patientId={id} canManageMonitoring />
                        </div>
                    )}

                    {id && mountedTabs.has('files') && (
                        <div
                            className={`flex flex-col flex-1 min-h-0 ${activeTab === 'files' ? '' : 'hidden'}`}
                        >
                            <FilesTabContent
                                patientId={id}
                                patientName={patientDetail?.name}
                                initialPreviewFileIds={previewFileIds}
                                onPreviewChange={handlePreviewChange}
                            />
                        </div>
                    )}

                    {id && mountedTabs.has('conversations') && (
                        <div
                            className={`flex flex-col flex-1 min-h-0 ${activeTab === 'conversations' ? '' : 'hidden'}`}
                        >
                            <ChatModeToggle
                                value={chatMode}
                                onChange={setChatMode}
                                internalDisabled={!cardData?.boards?.orthoItemId}
                                hasUnreadExternal={hasUnreadExternal}
                                hasUnreadInternal={hasUnreadInternal}
                            />
                            <PatientChatProvider
                                key={`${chatMode}-${chatApiVersion}`}
                                patientId={id}
                                service={getChatService(chatApiVersion)}
                                chatType={chatMode}
                                includeActivities
                            >
                                <ConversationActionsBar patientId={id} chatType={chatMode} />
                                <ChatTab chatType={chatMode} />
                            </PatientChatProvider>
                        </div>
                    )}

                    {patientDetail && id && mountedTabs.has('actions') && (
                        <div
                            className={`flex flex-col flex-1 min-h-0 ${activeTab === 'actions' ? '' : 'hidden'}`}
                        >
                            <ActionsTab
                                patientId={id}
                                patientName={patientDetail.name}
                                orthopulseStatus={cardData?.orthopulse?.status ?? null}
                                initialActionId={readActionFromSearch(location.search)}
                                onDataChange={() => {
                                    refreshAssets();
                                    refetchCard();
                                }}
                            />
                        </div>
                    )}

                    {/* Outside the tabs, so the URL-driven DM control survives a tab switch. */}
                    {patientDetail && id && (
                        <DmControlRoute patientId={id} patientName={patientDetail.name} />
                    )}

                    {id && mountedTabs.has('analysis') && (
                        <div
                            className={`flex flex-col flex-1 min-h-0 ${activeTab === 'analysis' ? '' : 'hidden'}`}
                        >
                            <AnalysisTab patientId={id} />
                        </div>
                    )}

                    {id && mountedTabs.has('info') && (
                        <div
                            className={`flex flex-col flex-1 min-h-0 ${activeTab === 'info' ? '' : 'hidden'}`}
                        >
                            <InfoTab patientId={id} />
                        </div>
                    )}

                    {id && mountedTabs.has('notes') && (
                        <div
                            className={`flex flex-col flex-1 min-h-0 ${activeTab === 'notes' ? '' : 'hidden'}`}
                        >
                            <NotesTab patientId={id} />
                        </div>
                    )}
                </div>

                {/* Right: Patient timeline rail (collapsible, persists state in localStorage) */}
                {id && <TimelineRail patientId={id} />}
            </div>
        </div>
    );
};

export { PatientFiles };
